import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { intakeLookupColumn, type IntakeTarget } from './useSubsidieIntake';
import type { SubsidieRegeling } from './useSubsidieRegelingen';
import { evaluateAllSubsidies, type SubsidieMatchStatus } from './subsidie-evaluatie-core';

/**
 * De rekenkern is per 8 september 2026 verhuisd naar
 * `./subsidie-evaluatie-core.ts`. Reden: die kern is puur en moet los van React
 * en Supabase te laden zijn, zodat `tests/subsidie/evaluatie.node.test.mjs` de
 * zes gemeten defecten kan bewaken zonder de app te starten. Dit bestand houdt
 * wat het altijd was — de hook die de evaluatie ophaalt en wegschrijft — en
 * exporteert `evaluateAllSubsidies` ongewijzigd door, zodat alle bestaande
 * imports (`SubsidieDashboard`, `SubsidieTab`, `DossierTab`,
 * `BusinesscaseDockTab`) blijven werken.
 */
export { evaluateAllSubsidies } from './subsidie-evaluatie-core';
export type { SubsidieMatchStatus, SubsidieEvaluatieResultaat } from './subsidie-evaluatie-core';

export interface SubsidieMatch {
  id: string;
  intake_id: string;
  regeling_id: string;
  match_status: SubsidieMatchStatus;
  geschat_subsidie: number;
  geschat_fiscaal: number;
  geschat_totaal: number;
  berekening: Record<string, any>;
  onvervulde_regels: Array<{ beschrijving: string; foutmelding: string }>;
  aanbevelingen: string[];
  berekend_op: string;
  // joined
  regeling?: SubsidieRegeling;
}

// --- React hook to evaluate + persist matches ---

export function useEvaluateSubsidies(target: IntakeTarget) {
  const queryClient = useQueryClient();

  const kolom = intakeLookupColumn(target);

  const matchesQuery = useQuery({
    queryKey: ['sub_matches', target.kind, target.id],
    queryFn: async () => {
      // First get intake
      const { data: intake } = await supabase
        .from('sub_intakes')
        .select('id')
        .eq(kolom, target.id)
        .maybeSingle();

      if (!intake) return [];

      const { data, error } = await supabase
        .from('sub_matches')
        .select('*, sub_regelingen(*)')
        .eq('intake_id', intake.id);
      if (error) throw error;

      return (data ?? []).map(m => ({
        ...m,
        regeling: m.sub_regelingen as unknown as SubsidieRegeling,
      })) as unknown as SubsidieMatch[];
    },
    enabled: !!target.id,
  });

  const evaluateMutation = useMutation({
    mutationFn: async (params: {
      intakeId: string;
      results: ReturnType<typeof evaluateAllSubsidies>;
    }) => {
      const { intakeId, results } = params;

      // Delete existing matches for this intake
      await supabase.from('sub_matches').delete().eq('intake_id', intakeId);

      // Insert new matches
      // `ongetoetst` gaat bewust NIET mee: de kolom bestaat niet in
      // `sub_matches` en de vlag is af te leiden uit het ontbreken van regels.
      const rows = results.map(r => ({
        intake_id: intakeId,
        regeling_id: r.regeling.id,
        match_status: r.match_status,
        geschat_subsidie: r.regeling.type === 'subsidie' ? r.geschat_totaal : 0,
        geschat_fiscaal: r.regeling.type === 'fiscaal' ? r.geschat_totaal : 0,
        geschat_totaal: r.geschat_totaal,
        berekening: r.berekening,
        onvervulde_regels: r.onvervulde_regels,
        aanbevelingen: r.aanbevelingen,
        berekend_op: new Date().toISOString(),
      }));

      if (rows.length > 0) {
        const { error } = await supabase.from('sub_matches').insert(rows);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub_matches', target.kind, target.id] });
    },
  });

  return {
    matches: matchesQuery.data ?? [],
    isLoading: matchesQuery.isLoading,
    evaluate: evaluateMutation.mutateAsync,
    isEvaluating: evaluateMutation.isPending,
  };
}
